<template>
    <label
        class="progress-bar"
        :class="labelClasses">

        <span class="progress-bar__input-wrapper">
            <input
                type="range"
                disabled
                v-bind="inputConfig"
                v-model="inputValue"/>
            <span
                :style="spanStyle"></span>
        </span>

        <span class="progress-bar__value"
            v-if="withValue">{{ animatedValue }}% </span>
    </label>
</template>

<script>
import includes from 'lodash/includes'
import { gsap } from 'gsap'

export default {
    name: 'ProgressBar',
    inheritAttrs: false,
    props: {
        value: {
            type: Number || String,
            default: 0
        },
        linear: {
            type: Boolean,
            default: false
        },
        /**
         * Магический параметр, отменяющий linear при загрузке и изменении
         * @type {Boolean}
         */
        sensitive: {
            type: Boolean,
            default: false
        },
        withValue: {
            type: [Boolean, Number],
            default: false
        },
        maxValue: {
            type: Number || String,
            default: 100
        },
        direction: {
            type: String,
            default: 'horizontal',
            validator(value) {
                return includes(['horizontal', 'vertical'], value)
            }
        },
        trackColor: {
            type: String,
            default: 'violet',
            validator(value) {
                return includes(
                    ['blue', 'violet', 'green', 'orange', 'pink', 'red', 'white'],
                    value
                )
            }
        },
        duration: {
            type: Number,
            default: 0.4
        },
        hovered: {
            type: Boolean,
            default: false
        },
        checked: {
            type: Boolean,
            default: false
        },
        calculate: {
            type: Boolean,
            default: false
        },
        deferredAnimation: {
            type: Boolean,
            default: false
        },
        // inputConfig: {
        //     type: Object,
        //     default: () => ({
        //         min: 0,
        //         max: 100,
        //         step: 1,
        //     })
        // }
    },
    data() {
        return {
            inputValue: 0,
            tweenedValue: 0
        }
    },
    computed: {
        isVertical() {
            return this.direction === 'vertical'
        },
        inputConfig() {
            return {
                min: 0,
                max: 100,
                step: 1,
            }
        },
        animatedValue() {
            return this.tweenedValue.toFixed(0)
        },
        animatedWidth() {
            return this.tweenedValue.toFixed(0) + '%'
        },
        labelClasses() {
            return {
                'progress-bar_vertical': this.isVertical,
                'progress-bar_track_violet': this.trackColor === 'violet',
                'progress-bar_track_blue': this.trackColor === 'blue',
                'progress-bar_track_orange': this.trackColor === 'orange',
                'progress-bar_track_pink': this.trackColor === 'pink',
                'progress-bar_track_white': this.trackColor === 'white',
                'progress-bar_track_green': this.trackColor === 'green',
                'progress-bar_track_red': this.trackColor === 'red',
                'progress-bar_checked': this.checked,
                'progress-bar_hovered': this.hovered,
                'progress-bar_empty': !this.inputValue,
            }
        },
        spanStyle() {
            return {
                width: this.animatedWidth
            }
        },
    },
    watch: {
        inputValue(value) {
            if (this.linear && !this.sensitive) return

            const calcVal = value * 100 / this.maxValue

            gsap.to(this.$data, this.duration, { tweenedValue: calcVal })
        },
        calculate(value) {
            if(value && this.deferredAnimation) {
                setTimeout(() => {
                    this.inputValue = this.value
                }, 500)
            }
        },
        value(value) {
            if (this.linear || this.sensitive) this.inputValue = this.tweenedValue = value
        }
    },
    mounted() {
        if(this.deferredAnimation === false) {
            setTimeout(() => {
                this.inputValue = this.value
            }, 500)
        }
    }
}

</script>

<style lang="less">
.progress-bar {
    --wrapper-color: @color-gray-white;

    display: flex;
    align-items: center;

    margin: 0;

    /*width: auto;*/

    position: relative;

    &_vertical {
        transform: rotateZ(90deg);
        transform-origin: left;
    }

    &_track {

        &_red {
            --track-color: @color-light-red;
        }

        &_blue {
            --track-color: @color-light-blue;
        }

        &_orange {
            --track-color: @color-light-orange;
        }

        &_green {
            --track-color: @color-light-green;
        }

        &_pink {
            --track-color: @color-light-pink;
        }

        &_white {
            --track-color: @color-white;
            --wrapper-color: rgba(255, 255, 255, .2);
        }

        &_violet {
            --track-color: @color-light-violet;
        }
    }

    &_hovered {
        cursor: pointer;

        input[disabled] {
            @{desktop} & {
                cursor: pointer;
            }
        }
    }

    &_empty {
        cursor: default;

        input {
            cursor: default !important;
        }
    }

    &_hovered:hover,
    .progress-group__item:hover &_hovered {

        &.progress-bar_track {
            &_red {
                @media @hover {
                    --track-color: @color-red;
                }
            }

            &_blue {
                @media @hover {
                    --track-color: @color-blue;
                }
            }

            &_orange {
                @media @hover {
                    --track-color: @color-orange;
                }
            }

            &_green {
                @media @hover {
                    --track-color: @color-green;
                }
            }

            &_pink {
                @media @hover {
                    --track-color: @color-pink;
                }
            }

            &_violet {
                @media @hover {
                    --track-color: @color-violet;
                }
            }
        }
    }

    &_checked {
        &.progress-bar_track {
            &_red {
                --track-color: @color-red;
            }

            &_blue {
                --track-color: @color-blue;
            }

            &_orange {
                --track-color: @color-orange;
            }

            &_green {
                --track-color: @color-green;
            }

            &_pink {
                --track-color: @color-pink;
            }

            &_violet {
                --track-color: @color-violet;
            }
        }
    }

    &__input-wrapper {
        display: block;
        flex: 1;

        background-color: var(--wrapper-color);
        border-radius: rem(3);

        height: rem(6);

        input {
            display: block;
            cursor: pointer;

            height: 100%;
            width: 100%;

            position: absolute;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
            z-index: 1;

            opacity: 0;
        }

        span {
            display: flex;
            align-items: center;
            justify-content: center;

            background-color: var(--track-color);
            border-radius: inherit;
            font-size: 10px;

            height: 100%;

            transition: background-color .15s ease;
        }

        input[disabled] {
            pointer-events: none;
            cursor: default;
        }
    }

    &__value {
        font-size: rem(13);
        text-align: right;

        margin-left: rem(13);

        width: rem(30);

        @{desktop} & {
            margin-left: rem(10);
        }
    }
}
</style>
